import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ViewYoutube } from '../../../services/viewYoutubeService';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Trash2, ExternalLink, Eye, MoreHorizontal, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';

interface CheckViewTableProps {
    videos: ViewYoutube[];
    loading: boolean;
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
    setPage: (p: number) => void;
    onDelete: (id: number) => void;
    onEdit: (video: ViewYoutube) => void;
}

const ExpandableText: React.FC<{ text: string, maxLength?: number, href?: string }> = ({ text, maxLength = 50, href }) => {
    const [expanded, setExpanded] = useState(false);

    if (!text) return <span>-</span>;

    const shouldTruncate = text.length > maxLength;
    const displayText = !shouldTruncate || expanded ? text : `${text.slice(0, maxLength)}...`;

    return (
        <div className="flex flex-col items-start gap-1">
            {href ? (
                <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
                    {displayText}
                </a>
            ) : (
                <span className="break-words">{displayText}</span>
            )}
            {shouldTruncate && (
                <button
                    onClick={() => setExpanded(!expanded)}
                    className="text-xs text-mutted-foreground hover:text-blue-500 font-medium"
                >
                    {expanded ? 'Collapse' : 'View more'}
                </button>
            )}
        </div>
    );
};

export const CheckViewTable: React.FC<CheckViewTableProps> = ({
    videos, loading, page, perPage, total, totalPages, setPage, onDelete, onEdit
}) => {
    const { t } = useTranslation('common');

    const formatNumber = (num?: number) => new Intl.NumberFormat('vi-VN').format(num || 0);
    const formatDate = (dateString?: string) => dateString ? new Date(dateString).toLocaleString('vi-VN') : '-';

    if (loading) {
        return <div className="p-8 text-center text-gray-500">{t('checkView.form.checking')}</div>;
    }

    if (videos.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center p-12 text-gray-400 border rounded-lg bg-gray-50 border-dashed">
                <div className="p-4 bg-white rounded-full shadow-sm mb-4">
                    <Eye className="w-8 h-8 text-gray-300" />
                </div>
                <p className="text-lg font-medium">{t('checkView.management.empty')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Desktop Table */}
            <div className="hidden md:block rounded-md border shadow-sm bg-white overflow-hidden">
                <Table>
                    <TableHeader className="bg-gray-50">
                        <TableRow>
                            <TableHead className="w-[50px]">{t('checkView.table.stt')}</TableHead>
                            <TableHead className="max-w-[200px]">{t('checkView.table.link')}</TableHead>
                            <TableHead className="max-w-[200px]">{t('checkView.table.title')}</TableHead>
                            <TableHead>{t('checkView.table.channel')}</TableHead>
                            <TableHead className="text-right">{t('checkView.table.views')}</TableHead>
                            <TableHead>{t('checkView.table.country')}</TableHead>
                            <TableHead>{t('checkView.table.status')}</TableHead>
                            <TableHead>{t('checkView.table.lastChecked')}</TableHead>
                            <TableHead className="text-right">{t('checkView.table.actions')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {videos.map((video, index) => (
                            <TableRow key={video.id} className="hover:bg-slate-50 transition-colors">
                                <TableCell>{(page - 1) * perPage + index + 1}</TableCell>
                                <TableCell>
                                    <ExpandableText text={video.Linkvideo || ''} href={video.Linkvideo} />
                                </TableCell>
                                <TableCell>
                                    <ExpandableText text={video.Content || video.NameChannel || ''} />
                                </TableCell>
                                <TableCell>{video.NameChannel || '-'}</TableCell>
                                <TableCell className="text-right font-semibold">
                                    {formatNumber(video.View)}
                                </TableCell>
                                <TableCell>{video.Country || '-'}</TableCell>
                                <TableCell>
                                    <Badge variant={video.status === 'active' ? 'default' : 'secondary'}
                                        className={`${video.status === 'active' ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                    >
                                        {video.status === 'active' ? t('checkView.management.active') : t('checkView.management.inactive')}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-xs text-gray-500">
                                    {formatDate(video.last_checked_at)}
                                </TableCell>
                                <TableCell className="text-right">
                                    <div className="flex justify-end gap-2">
                                        <Button variant="ghost" size="icon" onClick={() => onEdit(video)} className="h-8 w-8 text-blue-600">
                                            <ExternalLink className="h-4 w-4" />
                                        </Button>
                                        <Button variant="ghost" size="icon" onClick={() => video.id && onDelete(video.id)} className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50">
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {/* Mobile List */}
            <div className="md:hidden space-y-4">
                {videos.map((video, index) => (
                    <Card key={video.id} className="overflow-hidden">
                        <CardHeader className="p-4 bg-gray-50 flex flex-row justify-between items-center space-y-0">
                            <div className="flex items-center gap-2">
                                <span className="font-bold text-gray-500">#{(page - 1) * perPage + index + 1}</span>
                                <Badge variant={video.status === 'active' ? 'default' : 'secondary'} className={video.status === 'active' ? 'bg-green-100 text-green-700' : ''}>
                                    {video.status === 'active' ? t('checkView.management.active') : t('checkView.management.inactive')}
                                </Badge>
                            </div>
                            <div className="flex gap-1">
                                <Button variant="ghost" size="sm" onClick={() => onEdit(video)}>
                                    <ExternalLink className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => video.id && onDelete(video.id)} className="text-red-500">
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="p-4 space-y-3">
                            <div>
                                <p className="text-xs text-gray-500 uppercase font-semibold">{t('checkView.table.title')}</p>
                                <p className="font-medium text-sm">{video.Content || '-'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 uppercase font-semibold">{t('checkView.table.link')}</p>
                                <a href={video.Linkvideo} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 break-all">
                                    {video.Linkvideo}
                                </a>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <p className="text-xs text-gray-500 uppercase font-semibold">{t('checkView.table.views')}</p>
                                    <p className="font-bold text-lg text-blue-700">{formatNumber(video.View)}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 uppercase font-semibold">{t('checkView.table.channel')}</p>
                                    <p className="text-sm">{video.NameChannel || '-'}</p>
                                </div>
                            </div>
                            {video.last_checked_at && (
                                <div className="pt-2 border-t text-xs text-right text-gray-400">
                                    {t('checkView.table.lastChecked')}: {formatDate(video.last_checked_at)}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-6">
                    <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page <= 1}>
                        <ChevronLeft className="h-4 w-4 mr-1" /> Prev
                    </Button>
                    <div className="text-sm text-gray-600 font-medium">
                        Page {page} of {totalPages}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages}>
                        Next <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                </div>
            )}
        </div>
    );
};
